import type { DrawerState, DrawerPosition } from '@app/shared/types/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { useDrawer } from '@app/shared/pinia/drawer'
import getPiniaInstance from '@app/shared/pinia/instance'

import announceDrawerTransitions from './announce-transitions'

const collapseDrawer = (
  drawer: HTMLElement,
  modifier: DrawerPosition
): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__expanded--${modifier}`)
    drawer.classList.add(`sf-sidebar__collapsed--${modifier}`)
  })
}

const expandDrawer = (drawer: HTMLElement, modifier: DrawerPosition): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__collapsed--${modifier}`)
    drawer.classList.add(`sf-sidebar__expanded--${modifier}`)
  })
}

export default (doc: Document): Array<() => void> => {
  const unsubscribers: Array<() => void> = []

  const leftDrawer = doc.getElementById('left-drawer')
  const rightDrawer = doc.getElementById('right-drawer')
  const mainContent = doc.getElementById('main-content')

  const hasAnyDrawer = leftDrawer || rightDrawer

  if (!hasAnyDrawer) {
    return []
  }

  // Stop animating the main content after the animation ends
  // to prevent the main content to be animated when the window is resized
  const handleMainContentClass = (): void => {
    mainContent?.classList.remove('two-drawer-layout__main--transition')
  }

  for (const drawer of [leftDrawer, rightDrawer]) {
    if (!drawer) continue

    unsubscribers.push(announceDrawerTransitions(drawer).unsubscribe)

    drawer.addEventListener('transitionend', handleMainContentClass)
    unsubscribers.push(() =>
      drawer.removeEventListener('transitionend', handleMainContentClass)
    )
  }

  const isDrawerCollapsed = (
    drawer: HTMLElement | null,
    position: DrawerPosition
  ): boolean => {
    if (!drawer) {
      return false
    }

    return drawer.classList.contains(`sf-sidebar__collapsed--${position}`)
  }

  const closeDrawer = (position: DrawerPosition): void => {
    const drawer = position === 'left' ? leftDrawer : rightDrawer

    if (!drawer || isDrawerCollapsed(drawer, position)) {
      return
    }

    mainContent?.classList.add('two-drawer-layout__main--transition')

    if (!hasAnyDrawer) {
      mainContent?.classList.remove('two-drawer-layout__main--sidebar-expanded')
    }

    collapseDrawer(drawer, position)
  }

  const openDrawer = (position: DrawerPosition): void => {
    const drawer = position === 'left' ? leftDrawer : rightDrawer

    if (!drawer || !isDrawerCollapsed(drawer, position)) {
      return
    }

    mainContent?.classList.add('two-drawer-layout__main--transition')
    mainContent?.classList.add('two-drawer-layout__main--sidebar-expanded')

    expandDrawer(drawer, position)
  }

  const handleDrawerOpen = (drawer: DrawerState): void => {
    if (drawer.direction !== 'left' && drawer.direction !== 'right') {
      return
    }

    const oppositeDrawer = drawer.direction === 'left' ? 'right' : 'left'

    openDrawer(drawer.direction)
    closeDrawer(oppositeDrawer)

    if (mainContent && rightDrawer) {
      window.requestAnimationFrame(() => {
        if (
          drawer.content === RightDrawerContent.FileAttachments ||
          drawer.content === RightDrawerContent.FilePreview
        ) {
          mainContent.classList.add(
            'two-drawer-layout__main--with-file-attachments-sidebar'
          )
          rightDrawer.classList.add('sf-sidebar__file-attachments')
        } else {
          mainContent.classList.remove(
            'two-drawer-layout__main--with-file-attachments-sidebar'
          )
          rightDrawer.classList.remove('sf-sidebar__file-attachments')
        }
      })
    }
  }

  const handleDrawerClose = (): void => {
    closeDrawer('left')
    closeDrawer('right')

    mainContent?.classList.remove('two-drawer-layout__main--sidebar-expanded')
    mainContent?.classList.remove(
      'two-drawer-layout__main--with-file-attachments-sidebar'
    )
  }

  window.eventHub.$on('drawer:open', handleDrawerOpen)
  window.eventHub.$on('drawer:close', handleDrawerClose)

  // A page component may have opened a drawer through the store before this
  // ran: the two live in different rspack chunks, so their order on the page
  // is not fixed. The store's `drawer:open` for that call is already gone, so
  // apply its current state here.
  handleDrawerOpen(useDrawer(getPiniaInstance()).open)

  window.eventHub.$emit('drawer:ready')

  unsubscribers.push(() => {
    window.eventHub.$off('drawer:open', handleDrawerOpen)
    window.eventHub.$off('drawer:close', handleDrawerClose)
  })

  return unsubscribers
}
